/* eslint-disable perfectionist/sort-modules */
/* eslint-disable perfectionist/sort-imports */
/* eslint-disable no-unused-vars */

import { formatCellphoneMasked } from '@solfacil/frontend-utils/cellphone';
import {
  formatCnpjMasked,
  normalizeCnpjAlphanumeric,
} from '@solfacil/frontend-utils/cnpj';
import { formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils/cpf';
import { normalizeEmail } from '@solfacil/frontend-utils/email';
import { formatPhoneMasked } from '@solfacil/frontend-utils/phone';
import { sanitizeDigits } from '@solfacil/frontend-utils/utils';

/**
 * Generic fallback formatter for empty values
 * @param value - Value to check
 * @param fallback - Fallback value (default: '-')
 * @returns Original value or fallback
 */
const withFallback = (value: null | string | undefined, fallback = '-'): string => value || fallback;

/**
 * Format CNPJ for display (supports alphanumeric CNPJ)
 * @param cnpj - Masked or normalized CNPJ
 * @returns Formatted CNPJ or '-' if empty
 */
export const formatCNPJForDisplay = (cnpj: string): string => {
  if (!cnpj?.trim()) return '-';
  const normalized = normalizeCnpjAlphanumeric(cnpj);
  if (!normalized) return '-';
  return withFallback(formatCnpjMasked(normalized));
};

/**
 * Format CPF (11 digits) or CNPJ for display
 * @param document - Masked or normalized CPF/CNPJ
 * @returns Formatted document or '-' if empty
 */
export const formatDocumentForDisplay = (document: string): string => {
  if (!document?.trim()) return '-';
  const digits = normalizeCpfDigits(document);
  if (digits.length === 11) return formatCpfMasked(digits);
  return formatCNPJForDisplay(document);
};

/**
 * Format name for display (capitalize first letter of each word)
 * @param name - Unformatted name
 * @returns Formatted name or '-' if empty
 */
export const formatNameForDisplay = (name: string): string => {
  if (!name) return '-';

  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format phone number with Brazilian patterns (mobile 11 digits or landline 10)
 * @param phone - Phone with optional country code / mask
 * @returns Formatted phone or '-' if empty
 */
export const formatPhoneForDisplay = (phone: string): string => {
  if (!phone) return '-';

  const digits = sanitizeDigits(phone);
  if (digits.length === 10) return formatPhoneMasked(digits);
  if (digits.length === 11) return formatCellphoneMasked(digits);
  return phone;
};

/**
 * Format email for display
 * @param email - Email
 * @returns Normalized email or '-' if empty
 */
export const formatEmailForDisplay = (email: string): string => (
  withFallback(email?.trim() ? normalizeEmail(email) : null)
);

/**
 * Format monetary values for display
 * @param value - Numeric value
 * @param currency - Currency (default: 'BRL')
 * @returns Formatted currency value or '-' if empty/invalid
 */
export const formatCurrencyForDisplay = (value: number | string, currency = 'BRL'): string => {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (Number.isNaN(numericValue)) return '-';

  return new Intl.NumberFormat('pt-BR', {
    currency,
    style: 'currency',
  }).format(numericValue);
};

/**
 * Type for values that can be formatted
 */
export type FormattableValue = null | number | string | undefined;

/**
 * Default fallback value for empty fields
 */
export const DEFAULT_FALLBACK = '—' as const;

/**
 * Common units used in formatting
 */
export const UNITS = {
  KILOWATTS: 'kW',
  PERCENT: '%',
  VOLTS: 'V',
  WATTS: 'W',
} as const;

/**
 * Check if a value is considered empty
 * @param value - Value to check
 * @returns True if value is null, undefined, empty string, or whitespace only
 */
export const isEmpty = (value: FormattableValue): boolean => {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return typeof value === 'string' && !value.trim();
};

/**
 * Generic fallback formatter for any value type
 * @param value - Value to format
 * @param fallback - Fallback value (default: DEFAULT_FALLBACK)
 * @returns Original value or fallback if empty
 */
export function getValueOrFallback<T extends FormattableValue>(
  value: T,
  fallback: string = DEFAULT_FALLBACK,
): string | T {
  return isEmpty(value) ? fallback : value;
}

/**
 * Format a numeric value with a unit suffix
 * @param value - Numeric value to format
 * @param unit - Unit to append (e.g., 'W', '%', 'V')
 * @param fallback - Fallback value if empty
 * @returns Formatted value with unit or fallback
 */
export function formatValueWithUnit(
  value: null | number | undefined,
  unit: string,
  fallback: string = DEFAULT_FALLBACK,
): string {
  const formattedValue = getValueOrFallback(value, fallback);
  return formattedValue === fallback ? fallback : `${formattedValue}${unit}`;
}

/**
 * Operator-visible credential login for tables and modals (backend may already mask).
 */
export function formatCredentialLoginForDisplay(login: null | string | undefined): string {
  if (!login?.trim()) {
    return '—';
  }
  return login;
}

/**
 * Human-readable label for manufacture platform slugs (e.g. "growatt" → "Growatt").
 */
export function formatManufacturePlatformLabel(slug: string | undefined): string {
  if (!slug?.trim()) {
    return '—';
  }
  return slug
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format a string value with fallback
 * @param value - String value to format
 * @param fallback - Fallback value if empty
 * @returns Original string or fallback
 */
export function formatStringValue(
  value: string | undefined,
  fallback: string = DEFAULT_FALLBACK,
): string {
  return getValueOrFallback(value, fallback) as string;
}

/**
 * Format boolean value to 'Sim'/'Não'
 * @param value - Boolean value
 * @returns 'Sim' for true, 'Não' for false
 */
export function formatBooleanValue(value: boolean): string {
  return value ? 'Sim' : 'Não';
}

/**
 * Format SKUs array for table display (3 items per line)
 * @param skus - Array of SKU strings
 * @param fallback - Fallback value if empty
 * @returns SKUs grouped by line or fallback
 */
export function formatSkusValue(
  skus?: null | string[],
  fallback: string = DEFAULT_FALLBACK,
): string {
  if (!skus?.length) return fallback;

  const rows: string[] = [];
  for (let index = 0; index < skus.length; index += 3) {
    rows.push(skus.slice(index, index + 3).join(', '));
  }
  return rows.join('\n');
}
